import {
    faFaceFrown,
    faFaceLaugh,
    faFaceMeh,
    faFaceSmile,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    type ExtraInfoIcons,
    type CancellationTerm,
    type CardDescription,
    type CardLabel,
    type GenderLimitation,
    type PassengerCount,
    type ExtraInfoLang,
} from './types';
import type PriceType from './PriceType';
import type PriceClass from './PriceClass';
import type ItineraryOrder from './ItineraryOrder';
import type VerifyResult from '@itinerary/VerifyResult';
import { type AutomaticReserveDemandItemRequest } from '@clients/PricetrackApi';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import type BusPackage from '@itinerary/trip/bus/BusPackage';
import type FlightPackage from '@itinerary/trip/flight/FlightPackage';
import type TrainPackage from '@itinerary/trip/train/TrainPackage';
import type TrainTourPackage from '@itinerary/trip/train/TrainTourPackage';
import type TripPackage from '@itinerary/trip/TripPackage';
import { type InvoiceEntry } from '@store/invoice';
import { PaxType, type PaxTypeTitle } from '@utils/types';
import { ReserveType } from '@utils/itinerary';

export default abstract class ItineraryPackage<
    TPrice extends PriceClass = PriceClass,
> {
    /**
     * check to have safarmarket event or not
     *
     * @memberof ItineraryPackage
     */
    haveSmEvent = false;
    hasAutoReserve = false;

    get hasNeedHotelForm() {
        return false;
    }

    get autoReserveEnabled(): boolean {
        const { isMrbilit } = useWhiteLabel();
        return isMrbilit.value && this.hasAutoReserve && !this.reservable;
    }

    get optionalServiceTitle(): string | null {
        return null;
    }

    get freeServiceTitle(): string | null {
        return null;
    }

    /**
     * An array of CardLabel for show labels in card
     *
     * @type {CardLabel[]}
     * @memberof ItineraryPackage
     */
    get labels(): CardLabel[] {
        return [];
    }

    /**
     * An array of card description for show card details
     *
     * @readonly
     * @type {CardDescription[])}
     * @memberof ItineraryPackage
     */
    get packageInfoDescription(): CardDescription[] {
        return [];
    }

    get extraInfo():
        | {
              text: string | undefined;
              icon: ExtraInfoIcons;
              lang: ExtraInfoLang;
          }
        | undefined {
        return undefined;
    }

    /**
     * get a title for show in invoice table
     *
     * @readonly
     * @abstract
     * @type {string}
     * @memberof ItineraryPackage
     */
    abstract get invoiceTitle(): string;

    specialOffers: any[] = [];

    get isSpecialOffer(): boolean {
        return false;
    }

    get autoReserveDemandParams(): AutomaticReserveDemandItemRequest {
        throw new Error('Not implemented');
    }

    /**
     * if reserve-type supports real-time, needs to be implemented
     *
     * @memberof ItineraryPackage
     */
    uniqueId = '';

    passengerCountEditTypes: PaxTypeTitle[] = [
        {
            title: 'بزرگسال',
            code: PaxType.Adult,
        },
        {
            title: 'کودک',
            code: PaxType.Child,
        },
        {
            title: 'نوزاد',
            code: PaxType.Infant,
        },
    ];

    get hasPassengerDelete(): boolean {
        return true;
    }

    get hasPassengerDuplicate(): boolean {
        return false;
    }

    cancellationTerms: CancellationTerm[] = [];
    extraCancellationTerm = '';
    separateExtraTermsTab = false;

    /**
     * get the list of selected prices with their price class
     *
     * @readonly
     * @abstract
     * @memberof ItineraryPackage
     */
    abstract get selectedPricesWithPriceClass():
        | [TPrice, PriceType<TPrice>][]
        | null;

    abstract get raiseTitle(): string;

    /**
     * termsAndConditions of itinerary package
     *
     * @abstract
     * @type {string}
     * @memberof ItineraryPackage
     */
    abstract termsAndConditions: string | null;

    get termsAndConditionsTitle(): string {
        return 'شرایط و مقررات:';
    }

    /**
     * use can use a discount code on this package or not
     *
     * @type {boolean}
     * @memberof FlightPackage
     */
    canUseDiscount = true;

    /**
     * The prices that is selected in raise class page, and originally equals to defaultPrice.
     *
     * @type {TPrice | null}
     * @memberof ItineraryPackage
     */
    selectedPrices: TPrice[] | null = null;

    /**
     * The default price for itinerary package
     *
     * @type {TPrice | null}
     * @memberof ItineraryPackage
     */
    defaultPrice: TPrice | null = null;

    get raisePriceTypesTitle(): string {
        return '';
    }

    get raisePriceClassesTitle(): string {
        return '';
    }

    minPersons = 1;

    /**
     * Returns the total count of passengers, that the package is offered for
     *
     * @type {number}
     * @memberof ItineraryPackage
     */
    abstract get totalCount(): number;

    /**
     * Returns points of the trip packages, based on passengers' opinions
     *
     * @type {number | null}
     * @memberof ItineraryPackage
     */
    abstract get points(): number | null;

    get points10(): number | null {
        if (!this.points) {
            return null;
        }
        return Math.round((this.points || 0) * 20) / 10;
    }

    get pointIcon(): IconDefinition {
        if (!this.points10) {
            return faFaceMeh;
        }
        if (this.points10 <= 3.5) {
            return faFaceFrown;
        } else if (this.points10 <= 6.1) {
            return faFaceMeh;
        } else if (this.points10 <= 7) {
            return faFaceSmile;
        } else {
            return faFaceLaugh;
        }
    }

    /**
     * Returns the count of the points of the trip packages, based on passengers' opinions
     *
     * @type {number | null}
     * @memberof ItineraryPackage
     */
    pointsCount: number | null = null;

    /**
     * Returns the prices of trip package
     *
     * @type {TripPriceType<TPrice>[]}
     * @memberof ItineraryPackage
     */
    abstract prices: PriceType<TPrice>[];

    /**
     * Indicates whether to pin the trip package to top or not
     *
     * @type {boolean}
     * @memberof ItineraryPackage
     */
    pinToTop = false;

    /**
     * Indicates whether the trip package must be shown in promoted section of list, or not
     *
     * @type {boolean}
     * @memberof ItineraryPackage
     */
    isPromoted = false;

    needRefreshInvoiceOnChangeNationality = false;

    /**
     * Designates the order of the package in the list
     *
     * @type {ItineraryOrder}
     * @memberof ItineraryPackage
     */
    abstract get order(): ItineraryOrder;

    /**
     * Indicates whether it is needed to login for discount or not
     *
     * @type {boolean}
     * @memberof ItineraryPackage
     */
    abstract get needsLoginForDiscount(): boolean;

    /**
     * Returns tool tip about buying the trip package
     *
     * @type {string}
     * @memberof ItineraryPackage
     */
    abstract get buyTooltip(): string;

    get showTooltip(): boolean {
        return false;
    }

    get alertText(): string {
        return this.buyText;
    }

    /**
     * Returns the text shown on the reserve button in the list, for the
     * package.
     * <tr />
     * This is the only text shown on the button, whether it's available or not.
     * @type {string}
     * @memberof ItineraryPackage
     */
    get buyText(): string {
        return '';
    }

    get reserveError(): string {
        return '';
    }

    get showReserveErrorInDesktop(): boolean {
        return false;
    }

    get reserveButtonEnabled(): boolean {
        return this.reservable;
    }

    get gateWayMessage(): string | null {
        return null;
    }

    /**
     * Returns the total payable price
     *
     * @type {number | null}
     * @memberof TripPackage
     */
    abstract get totalPayableForCheck(): number | null;

    /**
     * Returns the total price
     *
     * @type {number | null}
     * @memberof TripPackage
     */
    abstract get totalPriceForCheck(): number | null;

    /**
     * Indicates whether the trip package is reservable or not.
     * <tr />
     * If false, then package is listed in `non reservable` section, below
     * reservable ones.
     *
     * @type {boolean}
     * @memberof ItineraryPackage
     */
    abstract get reservable(): boolean;

    /**
     * Returns the unique key, to be used in rendering list of trip packages.
     *
     * @type {string}
     * @memberof ItineraryPackage
     */
    abstract get key(): string;

    /**
     * Returns the payable price of the single package
     *
     * @type {number}
     * @memberof ItineraryPackage
     */
    abstract get singlePayable(): number;

    /**
     * Returns the price of the single package
     *
     * @type {number}
     * @memberof ItineraryPackage
     */
    abstract get singlePrice(): number;

    /**
     * check to this itinerary package need raise class or not
     *
     * @param {PassengerCount} count
     * @return {*}  {boolean}
     * @memberof ItineraryPackage
     */
    checkNeedsRaiseClass(count: PassengerCount): boolean {
        const availablePriceTypes = this.prices?.filter(p =>
            p.classes.some(c => c.capacity > 0)
        );
        const availableExtraOptions =
            this.selectedPricesWithPriceClass
                ?.map((priceT, index) => ({
                    title: priceT[1].title,
                    selectedPriceClassIndex: index,
                    list: priceT[0].getAvailableExtraOptions(count) || [],
                }))
                .filter(ex => ex.list.length) || [];
        return (
            availablePriceTypes.length > 1 ||
            availablePriceTypes.some(
                pt =>
                    pt.classes
                        ?.filter(p => p.capacity > 0)
                        ?.filter(p => p.isReservable).length > 1
            ) ||
            availableExtraOptions.length > 0
        );
    }

    abstract get reserveType(): ReserveType;

    get isFull() {
        return false;
    }

    get hasGenderLimitation(): GenderLimitation | false {
        return false;
    }

    /**
     * Indicates whether it is needed to call for trip package reservation or not
     *
     * @type {boolean}
     * @memberof ItineraryPackage
     */
    abstract get callToReserve(): boolean;
    /**
     * Indicates whether the card of the package must be emphasized
     * using the color specified in this field
     *
     * @type {(string | null)}
     */
    specialBorderColor: string | null = null;
    concreteType?: ReserveType;

    get cancellationMessage(): string | undefined {
        return undefined;
    }

    /**
     * Verifies the ability to reserve, and creates a new bill, and new reservation
     *
     * @type {Promise<VerifyResult>}
     * @memberof ItineraryPackage
     */
    abstract createBill(
        count: PassengerCount,
        billId: number | null
    ): Promise<VerifyResult>;

    /**
     * Loads the fares of different types of passengers of the trip
     *
     * @memberof ItineraryPackage
     */
    abstract loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
        loggedIn: boolean;
    }): Promise<InvoiceEntry[]>;

    get billMessage(): MessageItem[] {
        return [];
    }

    /**
     * @param pkg
     * @deprecated don't use it unless you want to pass the reference
     */
    static isTripPackage(pkg: ItineraryPackage): pkg is TripPackage {
        return pkg && pkg.concreteType !== ReserveType.Hotel;
    }

    isTripPackage(): this is TripPackage {
        return this.concreteType !== ReserveType.Hotel;
    }

    isHotelPackage(): this is HotelPackage {
        return this.concreteType === ReserveType.Hotel;
    }

    isFlightPackage(): this is FlightPackage {
        return this.concreteType === ReserveType.Flight;
    }

    isTrainPackage(): this is TrainPackage {
        return this.concreteType === ReserveType.Train;
    }

    isTrainTourPackage(): this is TrainTourPackage {
        return this.concreteType === ReserveType.TrainTour;
    }

    isBusPackage(): this is BusPackage {
        return this.concreteType === ReserveType.Bus;
    }

    isTaxiPackage(): this is BusPackage {
        return this.isBusPackage() && this.reserveType === ReserveType.Taxi;
    }

    toJSON() {
        return { ...this };
    }
}
